import { useReducer, useMemo, useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Loader2, LogIn } from 'lucide-react'
import { StepProgress, type Step } from '@meiuca/ui-components'
import { bookingReducer, initialState } from '../components/BookingModal/reducer'
import type { StepType, SummaryCheckoutPricing } from '../components/BookingModal/types'
import { ModalityStep } from '../components/BookingModal/steps/ModalityStep'
import { ScheduleStep } from '../components/BookingModal/steps/ScheduleStep'
import { SummaryStep } from '../components/BookingModal/steps/SummaryStep'
import { arenaService } from '@/services/arenaService'
import { reservationService } from '@/services/reservationService'
import {
  venueBookingService,
  type CheckoutPaymentMethod,
  type CheckoutPaymentMethodId
} from '@/services/venueBookingService'
import type { VenueDetailResponse } from '@/types/sdui-arena-detail'
import type { ReservationItem } from '@/services/reservationService'
import { MarketplaceHeader } from '@/components/MarketplaceHeader'
import { AuthModal } from '@/components/AuthModal'
import { useAuth } from '@/contexts/AuthContext'
import { formatDateForAPI } from '@meiuca/web-foundation'
import { analyticsService } from '@meiuca/analytics'
import { ANALYTICS_EVENTS, SCREEN_NAMES } from '@/constants/analyticsEvents'
import { toastManager } from '@/lib/toastManager'

// Deduplicate fetches in dev StrictMode
const venueDetailRequests = new Map<string, Promise<VenueDetailResponse>>()

async function fetchVenueDetailOnce(venueId: string) {
  const existing = venueDetailRequests.get(venueId)
  if (existing) return existing

  const request = arenaService.getVenueDetail(venueId).finally(() => {
    venueDetailRequests.delete(venueId)
  })
  venueDetailRequests.set(venueId, request)
  return request
}

function BookingAuthRequired({ onLoginTap }: { onLoginTap: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center text-center px-6 py-16 gap-4">
      <div className="w-16 h-16 rounded-full bg-brand-primary/10 flex items-center justify-center">
        <LogIn className="w-7 h-7 text-brand-primary" />
      </div>
      <div className="space-y-1">
        <h2 className="text-lg font-semibold text-text-primary">Entre para finalizar a reserva</h2>
        <p className="text-sm text-text-secondary max-w-xs">
          Para confirmar sua reserva, é preciso estar logado na sua conta.
        </p>
      </div>
      <button
        type="button"
        onClick={onLoginTap}
        className="min-w-[200px] px-4 py-2.5 rounded-lg bg-brand-primary text-white font-medium hover:bg-brand-primary/90"
      >
        Entrar
      </button>
    </div>
  )
}

export function BookingPage() {
  const { venueId } = useParams<{ venueId: string }>()
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const [state, dispatch] = useReducer(bookingReducer, initialState)
  const [isConfirming, setIsConfirming] = useState(false)
  const [venueConfig, setVenueConfig] = useState<VenueDetailResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [showLoginModal, setShowLoginModal] = useState(false)

  // Payment / checkout-options state (last step)
  const [paymentMethods, setPaymentMethods] = useState<CheckoutPaymentMethod[]>([])
  const [paymentsLoading, setPaymentsLoading] = useState(false)
  const [paymentsError, setPaymentsError] = useState(false)
  const [selectedMethodId, setSelectedMethodId] = useState<CheckoutPaymentMethodId | null>(null)
  const [checkoutPricing, setCheckoutPricing] = useState<SummaryCheckoutPricing | null>(null)
  const [generatingPix, setGeneratingPix] = useState(false)

  // Extract venue info from the structured payload
  const venueInfo = useMemo(() => {
    if (!venueConfig) return null
    return {
      name: venueConfig.header.name,
      logoUrl: venueConfig.header.logoUrl
    }
  }, [venueConfig])

  // The route param may be a slug OR a UUID. Once the detail is loaded, use the
  // canonical venue UUID from the response for every downstream call (availability,
  // reservations) — those endpoints require the real id, not the slug.
  const resolvedVenueId = venueConfig?.venueId ?? venueId

  // Build the reservation items + total once, reused by checkout-options and create.
  const { reservationItems, totalPriceCents } = useMemo(() => {
    const items: ReservationItem[] = state.selectedSlotDetails.flatMap(slot =>
      slot.courts.map(court => {
        const [hours, minutes] = slot.time.split(':').map(Number)
        // "YYYY-MM-DD" parsed as local midnight (avoids UTC day shift).
        const startDate = new Date(slot.date + 'T00:00:00')
        startDate.setHours(hours, minutes, 0, 0)
        const endDate = new Date(startDate)
        endDate.setHours(hours + 1, minutes, 0, 0)
        return {
          courtId: court.id,
          modality: state.selectedModality ?? '',
          startsAt: formatDateForAPI(startDate),
          endsAt: formatDateForAPI(endDate),
          priceCents: court.pricePerHour || 0
        }
      })
    )
    const total = items.reduce((sum, item) => sum + item.priceCents, 0)
    return { reservationItems: items, totalPriceCents: total }
  }, [state.selectedSlotDetails, state.selectedModality])

  const selectedMethod = paymentMethods.find(m => m.id === selectedMethodId) ?? null
  const paymentReady = !!selectedMethod

  const completedRef = useRef(false)
  const stepRef = useRef<StepType>(state.currentStep)
  useEffect(() => {
    stepRef.current = state.currentStep
  }, [state.currentStep])

  // Booking abandonment: if the user leaves before completing, log where they
  // dropped (funnel drop-off). handleConfirm flips completedRef on success first.
  useEffect(() => {
    return () => {
      if (!completedRef.current) {
        analyticsService.track(ANALYTICS_EVENTS.BOOKING_ABANDONED, {
          venue_id: venueId,
          step: stepRef.current,
        })
      }
    }
  }, [])

  const handleSelectPaymentMethod = (id: CheckoutPaymentMethodId) => {
    analyticsService.track(ANALYTICS_EVENTS.BOOKING_PAYMENT_METHOD_SELECTED, {
      venue_id: resolvedVenueId,
      payment_method: id,
    })
    setSelectedMethodId(id)
  }

  const loadCheckoutOptions = useCallback(async () => {
    if (!resolvedVenueId || reservationItems.length === 0) return
    setPaymentsLoading(true)
    setPaymentsError(false)
    try {
      const res = await venueBookingService.getCheckoutOptions(resolvedVenueId, {
        priceCents: totalPriceCents,
        items: reservationItems
      })
      setPaymentMethods(res.paymentMethods)
      setSelectedMethodId(res.paymentMethods[0]?.id ?? null)

      // Valores de campanha (de→por por item + totais) — o resumo usa como
      // prévia autoritativa; itens chegam na ordem do request, mas casamos por
      // quadra+início para não depender disso.
      if (res.totalAmountCents != null) {
        const finalPriceByItemKey: Record<string, number> = {}
        for (const item of res.items ?? []) {
          finalPriceByItemKey[`${item.courtId}|${new Date(item.startsAt).getTime()}`] =
            item.finalPriceCents
        }
        setCheckoutPricing({
          finalPriceByItemKey,
          totalAmountCents: res.totalAmountCents,
          discountAmountCents: res.discountAmountCents ?? 0,
          finalAmountCents: res.finalAmountCents ?? res.totalAmountCents
        })
      } else {
        setCheckoutPricing(null)
      }
    } catch (err) {
      console.error('Error loading checkout options:', err)
      setPaymentsError(true)
    } finally {
      setPaymentsLoading(false)
    }
  }, [resolvedVenueId, reservationItems, totalPriceCents])

  // Fetch available payment methods when the user reaches the summary step — only
  // for logged-in users; logged-out users get a login prompt instead of the summary.
  useEffect(() => {
    if (state.currentStep !== 'summary' || !isAuthenticated) return
    loadCheckoutOptions()
  }, [state.currentStep, isAuthenticated, loadCheckoutOptions])

  useEffect(() => {
    let isActive = true

    // Track screen view
    analyticsService.trackScreenView(SCREEN_NAMES.BOOKING)

    async function fetchVenueDetail() {
      if (!venueId) {
        setIsLoading(false)
        return
      }

      try {
        setIsLoading(true)
        const config = await fetchVenueDetailOnce(venueId)
        if (isActive) {
          setVenueConfig(config)

          // Track booking started
          analyticsService.track(ANALYTICS_EVENTS.BOOKING_STARTED, {
            venue_id: venueId
          })
        }
      } catch (err) {
        console.error('Error fetching venue detail:', err)
      } finally {
        if (isActive) {
          setIsLoading(false)
        }
      }
    }

    fetchVenueDetail()

    return () => {
      isActive = false
    }
  }, [venueId])

  const steps: Step[] = useMemo(() => [
    { label: 'Modalidade', isComplete: !!state.selectedModality },
    { label: 'Horários', isComplete: state.selectedSlotDetails.length > 0 },
    { label: 'Resumo', isComplete: false }
  ], [state.selectedModality, state.selectedSlotDetails.length])

  const currentStepIndex = useMemo(() => {
    const stepMap: Record<StepType, number> = {
      modality: 0,
      schedule: 1,
      summary: 2
    }
    return stepMap[state.currentStep]
  }, [state.currentStep])

  const handleBack = () => {
    if (state.currentStep === 'modality') {
      navigate(-1)
    } else if (state.currentStep === 'summary') {
      dispatch({ type: 'setCurrentStep', payload: 'schedule' })
    } else if (state.currentStep === 'schedule') {
      dispatch({ type: 'setCurrentStep', payload: 'modality' })
    }
  }

  const handleStepClick = (index: number) => {
    const stepTypes: StepType[] = ['modality', 'schedule', 'summary']
    dispatch({ type: 'setCurrentStep', payload: stepTypes[index] })
  }

  const handleNext = () => {
    if (state.currentStep === 'modality' && state.selectedModality) {
      // Track modality selected
      analyticsService.track(ANALYTICS_EVENTS.BOOKING_MODALITY_SELECTED, {
        venue_id: venueId,
        modality: state.selectedModality
      })
      dispatch({ type: 'setCurrentStep', payload: 'schedule' })
    } else if (state.currentStep === 'schedule' && state.selectedSlotDetails.length > 0) {
      // Track schedule selected
      analyticsService.track(ANALYTICS_EVENTS.BOOKING_SCHEDULE_SELECTED, {
        venue_id: venueId,
        modality: state.selectedModality,
        slots_count: state.selectedSlotDetails.length,
        dates: state.selectedSlotDetails.map(s => s.date).join(',')
      })
      dispatch({ type: 'setCurrentStep', payload: 'summary' })
    }
  }

  // Poll the reservation payment-details until the Pix code is generated, so we
  // only navigate once it's ready (mirrors the mobile app).
  const waitForPixReady = async (reservationId: string): Promise<boolean> => {
    const maxAttempts = 20 // ~60s at 3s intervals
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const details = await reservationService.getReservationPaymentDetails(reservationId)
        const pix = details.paymentDetails?.pix
        if ((pix?.payload && pix?.encodedImage) || details.paymentStatus === 'paid') {
          return true
        }
      } catch {
        // Transient error — keep polling.
      }
      await new Promise(resolve => setTimeout(resolve, 3000))
    }
    return false
  }

  const handleConfirm = async () => {
    // Check if user is authenticated
    if (!isAuthenticated) {
      // Track login required
      analyticsService.track(ANALYTICS_EVENTS.BOOKING_LOGIN_REQUIRED, {
        venue_id: venueId,
        modality: state.selectedModality,
        slots_count: state.selectedSlotDetails.length
      })
      setShowLoginModal(true)
      return
    }

    if (!resolvedVenueId || !state.selectedModality) {
      console.error('BookingPage: Missing required data')
      return
    }

    setIsConfirming(true)
    try {
      const isPix = selectedMethod?.id === 'pix_online'

      // Create the reservation with the chosen payment method (omitted = manual).
      const result = await reservationService.createReservation({
        venueId: resolvedVenueId,
        priceCents: totalPriceCents,
        items: reservationItems,
        paymentMethod: isPix ? 'pix' : undefined
      })

      const reservationId = result.reservation.id

      completedRef.current = true

      // Track successful booking (CONVERSION!)
      analyticsService.track(ANALYTICS_EVENTS.BOOKING_COMPLETED, {
        venue_id: resolvedVenueId,
        modality: state.selectedModality,
        slots_count: state.selectedSlotDetails.length,
        total_price: totalPriceCents,
        payment_method: isPix ? 'pix' : 'manual',
        reservation_id: reservationId
      })

      // Pix: wait for the payment code to be generated before leaving (like mobile).
      if (isPix) {
        setGeneratingPix(true)
        const ready = await waitForPixReady(reservationId)
        if (!ready) {
          toastManager.info('O Pix está demorando a ser gerado. Abrindo sua reserva...')
        }
      }

      toastManager.success('Reserva criada com sucesso!')

      // Land on the reservation detail. For Pix the code is already generated, so
      // it shows immediately (autoPix).
      navigate(`/reservations/${reservationId}`, { state: { autoPix: isPix } })
    } catch (error) {
      console.error('📖 BookingPage: Error creating reservation:', error)

      // Track booking error
      analyticsService.track(ANALYTICS_EVENTS.BOOKING_ERROR, {
        venue_id: resolvedVenueId,
        modality: state.selectedModality,
        error_message: error instanceof Error ? error.message : 'Unknown error'
      })

      // Show error message to user
      toastManager.error('Erro ao criar reserva. Tente novamente.')
    } finally {
      setIsConfirming(false)
      setGeneratingPix(false)
    }
  }

  const canProceed =
    state.currentStep === 'modality'
      ? !!state.selectedModality
      : state.currentStep === 'schedule'
        ? state.selectedSlotDetails.length > 0
        : true

  const renderStep = () => {
    if (!resolvedVenueId) return null

    switch (state.currentStep) {
      case 'modality':
        return (
          <ModalityStep
            selectedModality={state.selectedModality}
            onSelectModality={(modality) => dispatch({ type: 'setSelectedModality', payload: modality })}
            venueId={resolvedVenueId}
          />
        )

      case 'schedule':
        return (
          <ScheduleStep
            selectedModality={state.selectedModality}
            selectedSlotDetails={state.selectedSlotDetails}
            onSelectSlots={(slots) => dispatch({ type: 'setSelectedSlotDetails', payload: slots })}
            venueId={resolvedVenueId}
          />
        )

      case 'summary':
        // Logged-out users get a login prompt instead of the summary, mirroring
        // the mobile apps.
        if (!isAuthenticated) {
          return <BookingAuthRequired onLoginTap={() => setShowLoginModal(true)} />
        }
        return (
          <SummaryStep
            selectedModality={state.selectedModality}
            selectedSlotDetails={state.selectedSlotDetails}
            checkoutPricing={checkoutPricing}
            paymentMethods={paymentMethods}
            loadingPayments={paymentsLoading}
            paymentsError={paymentsError}
            onRetryPayments={loadCheckoutOptions}
            selectedMethodId={selectedMethodId}
            onSelectMethod={handleSelectPaymentMethod}
          />
        )

      default:
        return null
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background-primary">
        <MarketplaceHeader />
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-brand-primary border-r-transparent mb-4" />
            <p className="text-text-secondary">Carregando...</p>
          </div>
        </div>
      </div>
    )
  }

  if (!venueId || !venueInfo) {
    return (
      <div className="min-h-screen bg-background-primary">
        <MarketplaceHeader />
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <p className="text-text-secondary mb-4">Arena não encontrada</p>
            <button
              onClick={() => navigate(-1)}
              className="text-brand-primary hover:underline"
            >
              Voltar
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background-primary flex flex-col">
      {/* Marketplace Header */}
      <MarketplaceHeader />

      {/* Booking Header with logo and name */}
      <div className="sticky top-[64px] z-40 bg-white border-b border-border-light">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={handleBack}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Voltar"
            >
              <ArrowLeft className="w-5 h-5 text-text-primary" />
            </button>
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {venueInfo.logoUrl && (
                <img
                  src={venueInfo.logoUrl}
                  alt={venueInfo.name}
                  className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                />
              )}
              <div className="min-w-0 flex-1">
                <h1 className="text-lg font-semibold text-text-primary truncate">
                  {venueInfo.name}
                </h1>
                <p className="text-sm text-text-secondary">Nova Reserva</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Step Progress */}
      <div className="px-6 pt-6 pb-4 border-b border-border-light bg-white max-w-7xl w-full mx-auto">
        <StepProgress
          steps={steps}
          currentStep={currentStepIndex}
          onStepClick={handleStepClick}
        />
      </div>

      {/* Step Content - with padding bottom for fixed summary + footer */}
      <div className="flex-1 overflow-y-auto bg-background-primary pb-36">
        <div className="max-w-7xl w-full mx-auto">
          {renderStep()}
        </div>
      </div>

      {/* Footer Buttons - Fixed at bottom (hidden on the logged-out summary prompt) */}
      {!(state.currentStep === 'summary' && !isAuthenticated) && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-border-light shadow-lg">
          <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center gap-3">
            <button
              type="button"
              className="min-w-[96px] px-4 py-2 rounded-lg border border-border-light text-text-primary bg-transparent hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
              onClick={handleBack}
              disabled={isConfirming}
            >
              Voltar
            </button>
            <button
              type="button"
              className="min-w-[120px] px-4 py-2 rounded-lg bg-brand-primary text-white font-medium hover:bg-brand-primary/90 disabled:opacity-60 disabled:cursor-not-allowed"
              onClick={state.currentStep === 'summary' ? handleConfirm : handleNext}
              disabled={
                (state.currentStep !== 'summary' && !canProceed) ||
                isConfirming ||
                (state.currentStep === 'summary' && (paymentsLoading || !paymentReady))
              }
            >
              {state.currentStep === 'summary' ? (
                isConfirming || generatingPix ? (
                  <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                ) : (
                  'Confirmar Reserva'
                )
              ) : (
                'Continuar'
              )}
            </button>
          </div>
        </div>
      )}

      {/* Auth Modal */}
      <AuthModal
        open={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onSuccess={() => setShowLoginModal(false)}
      />
    </div>
  )
}
